import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { sellCategories as fallbackCategories } from '../data/sellDeviceData';
import { Smartphone, Laptop, Watch, Gamepad2, ChevronRight, DollarSign, Shield, Clock, Zap, Banknote, Package, Tablet, HelpCircle, ArrowDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import GoogleReviewsBadge from '../components/shared/GoogleReviewsBadge';
import ScrollDiscoveryHint from '../components/shared/ScrollDiscoveryHint';
import SellFAQSection from '../shared/SellFAQSection';
import SellTrustSignals from '../shared/SellTrustSignals';
import SellInternalLinks from '../shared/SellInternalLinks';
import MobileStickyQuoteCTA from '../shared/MobileStickyQuoteCTA';
import ExitIntentPopup from '../shared/ExitIntentPopup';
import RelatedServices from '../shared/RelatedServices';
import InstantEstimate from '../components/InstantEstimate';
import SellHowItWorks from '../components/SellHowItWorks';
import BuybackBanners from '../components/BuybackBanners';
import { SellArchitectureBlock } from './SellArchitectureSections';
import { HomeContactChoice } from '../components/HomeContactChoice';
import { SellContactSupport } from '../components/sell/SellContactSupport';
import { BrowsePriceNotice } from '../shared/InstantEstimateNotice';

const API_URL = (() => { const envUrl = process.env.REACT_APP_BACKEND_URL; if (!envUrl || envUrl === '') return window.location.origin; try { const envHost = new URL(envUrl).hostname; const currentHost = window.location.hostname; if (currentHost === 'localhost' || currentHost === '127.0.0.1' || envHost === currentHost || currentHost.includes('preview.emergentagent.com')) { return envUrl; } return window.location.origin; } catch (e) { return window.location.origin; } })();

const iconMap = {
  Smartphone,
  Laptop,
  Watch,
  Gamepad2,
  Tablet,
  HelpCircle
};

const gradientMap = {
  phones: 'from-blue-500 to-cyan-400',
  'phones-sell': 'from-blue-500 to-cyan-400',
  laptops: 'from-purple-500 to-pink-400',
  'laptops-sell': 'from-purple-500 to-pink-400',
  watches: 'from-orange-500 to-amber-400',
  'watches-sell': 'from-orange-500 to-amber-400',
  consoles: 'from-green-500 to-emerald-400',
  'consoles-sell': 'from-green-500 to-emerald-400',
  tablets: 'from-indigo-500 to-violet-400',
  'tablets-sell': 'from-indigo-500 to-violet-400',
  other: 'from-slate-500 to-slate-400',
  'other-sell': 'from-slate-500 to-slate-400'
};

const SellDevicePage = () => {
  const navigate = useNavigate();
  const [sellCategories, setSellCategories] = useState(fallbackCategories);
  const [showCategories, setShowCategories] = useState(false);

  // Fetch sell categories from Product Manager API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/products/categories?type=sell`);
        if (response.ok) {
          const data = await response.json();
          if (data.categories && data.categories.length > 0) {
            const transformed = data.categories.map(cat => ({
              // Strip "-sell" suffix for navigation - SellCategoryPage will add it back
              id: cat.slug.replace('-sell', ''),
              name: cat.name,
              description: cat.description,
              icon: cat.icon || 'Smartphone'
            }));
            // Always append "Other" category for custom device sales
            const otherCategory = {
              id: 'other',
              name: 'Other Devices',
              description: 'Sell any other electronics - describe your device',
              icon: 'HelpCircle'
            };
            setSellCategories([...transformed, otherCategory]);
          }
        }
      } catch (error) {
        console.log('Using fallback sell categories');
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (categoryId) => {
    navigate(`/sell/${categoryId}`);
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-32 pb-32 relative overflow-hidden">
      <Helmet>
        <title>Sell Your Phone, Laptop, Tablet for Cash | 2A Electronics Chicago</title>
        <meta name="description" content="Get top dollar for your used iPhone, Samsung, MacBook, iPad, gaming console. Instant cash quotes, free diagnostics. Sell in-store or mail-in from anywhere in the US." />
        <link rel="canonical" href="https://2aelectronics.com/sell" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Device Buyback",
          "name": "Sell Your Device - 2A Electronics",
          "description": "Get instant cash for your used phones, laptops, tablets, and gaming consoles. Free diagnostics, same-day payment.",
          "url": "https://2aelectronics.com/sell",
          "provider": {"@type": "LocalBusiness", "name": "2A Electronics Service", "address": {"@type": "PostalAddress", "addressLocality": "Chicago", "addressRegion": "IL"}},
          "areaServed": [{"@type": "Country", "name": "US"}, {"@type": "City", "name": "Chicago"}]
        })}</script>
      </Helmet>
      <ScrollDiscoveryHint totalCount={sellCategories.length} label="categories" accentColor="green" />
      <style>{`
        @keyframes card-rise {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
      
      {/* Glowing Orbs — rendered as radial-gradient glows (a paint, not a CSS filter).
          iOS Safari drops large `filter: blur()` layers past its texture limit, which
          made the old blur-[120px] orbs paint as solid hard-edged discs that covered the
          hero. A radial-gradient fades to transparent so it can never become a hard disc. */}
      <div
        className="absolute top-40 left-1/3 w-96 h-96 rounded-full pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, rgba(22,163,74,0.20) 0%, rgba(22,163,74,0) 70%)' }}
      />
      <div
        className="absolute bottom-40 right-1/3 w-96 h-96 rounded-full pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle, rgba(5,150,105,0.20) 0%, rgba(5,150,105,0) 70%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Sell Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">Device</span>
          </h1>

          {/* Google Reviews Trust Badge, compact & slim for mobile */}
          <div className="mt-2 flex items-center justify-center gap-2 flex-wrap" data-testid="sell-page-trust-badge">
            <GoogleReviewsBadge variant="compact" />
            <a href="https://share.google/9RAR40lhoe1RijZzZ" target="_blank" rel="noopener noreferrer" className="text-yellow-400/70 hover:text-yellow-400 text-xs font-medium transition-colors">
              See reviews
            </a>
          </div>
          <div className="mt-3 flex justify-center">
            <HomeContactChoice kind="sell" idPrefix="sell-header" />
          </div>
        </div>

        {/* ─── Instant Estimate Tool ─── */}
        <div className="mb-8" id="instant-estimate-section" data-testid="instant-estimate-section">
          <Suspense fallback={
            <div className="rounded-2xl bg-slate-900/60 border border-slate-800 h-72 animate-pulse flex items-center justify-center">
              <div className="text-slate-500 text-sm">Loading instant offer…</div>
            </div>
          }>
            <InstantEstimate />
          </Suspense>
        </div>

        {/* ─── Manual category browse — collapsed by default (Instant Estimate is the primary flow) ─── */}
        <div className="flex flex-col items-center mb-8" data-testid="scroll-to-categories">
          <button
            onClick={() => setShowCategories(v => !v)}
            aria-expanded={showCategories}
            className="group inline-flex items-center gap-1.5 py-2 px-4 rounded-full border border-slate-700 text-slate-400 hover:text-slate-200 hover:border-slate-500 transition-colors"
            data-testid="toggle-manual-categories"
          >
            <span className="text-xs font-medium">Prefer to browse by category?</span>
            <ArrowDown className={`w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 transition-transform ${showCategories ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Categories Grid — hidden until the customer opts in */}
        {showCategories && (
        <div id="sell-categories-section" data-testid="sell-categories-section">
        <div className="flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-slate-700" />
          <span className="text-slate-500 text-sm font-medium uppercase tracking-wider">Or browse by category</span>
          <div className="flex-1 h-px bg-slate-700" />
        </div>
        <div className="max-w-3xl mx-auto mb-8">
          <BrowsePriceNotice />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-8">Select Your Device to Sell</h2>
        <div id="sell-categories" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {sellCategories.map((category, index) => {
            const IconComponent = iconMap[category.icon] || HelpCircle;
            const gradient = gradientMap[category.id] || 'from-slate-500 to-slate-400';
            return (
              <div
                key={category.id}
                onClick={() => handleCategoryClick(category.id)}
                className="group relative bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-slate-700 hover:bg-slate-900/80 hover:scale-[1.02] hover:shadow-2xl hover:shadow-green-500/10 opacity-0 animate-[card-rise_0.5s_ease-out_forwards]"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`sell-category-${category.id}`}
              >
                {/* Gradient Border Effect on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl`} />
                
                {/* Top Section with Gradient Background */}
                <div className={`relative h-32 bg-gradient-to-br ${gradient} p-6 flex items-center justify-center`}>
                  <div className="absolute inset-0 bg-black/20" />
                  <IconComponent className="w-16 h-16 text-white relative z-10 group-hover:scale-110 transition-transform duration-300" />
                  
                  {/* Money Badge */}
                  <div className="absolute top-3 right-3 flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                    <DollarSign className="w-3 h-3 text-white" />
                    <span className="text-white text-xs font-bold">TOP $$$</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2 flex items-center justify-between">
                    {category.name}
                    <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-green-400 group-hover:translate-x-1 transition-all" />
                  </h3>
                  <p className="text-slate-400 text-sm">{category.description}</p>
                </div>
              </div>
            );
          })}
        </div>
        </div>
        )}

        {/* How It Works - Redesigned */}
        <div className="mb-8"><SellContactSupport idPrefix="sell-details" /></div>
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white text-center mb-10">
            How It <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">Works</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: '01', icon: Smartphone, title: 'Select Device', desc: 'Choose your device model', color: 'from-blue-500 to-cyan-500' },
              { step: '02', icon: DollarSign, title: 'Get Quote', desc: 'Instant price estimate', color: 'from-green-500 to-emerald-500' },
              { step: '03', icon: Package, title: 'Ship or Visit', desc: 'Free prepaid shipping', color: 'from-purple-500 to-pink-500' },
              { step: '04', icon: Banknote, title: 'Get Paid', desc: 'Fast payment sent', color: 'from-orange-500 to-amber-500' }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                {/* Connector Line */}
                {idx < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 w-4 h-0.5 bg-gradient-to-r from-slate-700 to-slate-800" />
                )}
                
                <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 text-center hover:border-slate-700 transition-colors">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center`}>
                    <item.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-slate-600 text-xs font-mono mb-2">STEP {item.step}</div>
                  <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Animated Sell Flow Widget, progressive 3-step visual */}
        <div className="-mx-4 sm:-mx-6 lg:-mx-8 mb-16">
          <SellHowItWorks />
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {[
            { icon: Zap, title: 'Instant Quotes', desc: 'Get an offer in seconds', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
            { icon: Clock, title: 'Fast Payment', desc: 'Paid within 24 hours', color: 'text-green-500', bg: 'bg-green-500/10' },
            { icon: Shield, title: 'Data Security', desc: 'Secure data erasure', color: 'text-blue-500', bg: 'bg-blue-500/10' }
          ].map((benefit, idx) => (
            <div 
              key={idx}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 flex items-center gap-4 hover:border-slate-700 transition-colors"
            >
              <div className={`w-14 h-14 ${benefit.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <benefit.icon className={`w-7 h-7 ${benefit.color}`} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">{benefit.title}</h3>
                <p className="text-slate-400 text-sm">{benefit.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="relative bg-gradient-to-r from-green-900/50 via-emerald-900/50 to-green-900/50 rounded-2xl p-8 md:p-12 border border-green-800/50 overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:30px_30px]" />
          <div
            className="absolute top-0 right-0 w-64 h-64 rounded-full"
            style={{ backgroundImage: 'radial-gradient(circle, rgba(34,197,94,0.10) 0%, rgba(34,197,94,0) 70%)' }}
          />
          
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Ready to Get Paid?
              </h3>
              <p className="text-slate-300">
                Select your device above and get an instant quote. <span className="text-green-400 font-semibold">Free shipping included!</span>
              </p>
            </div>
            <Button 
              onClick={() => navigate('/mail-in')}
              className="bg-green-600 hover:bg-green-500 text-white px-8 py-6 text-lg rounded-xl flex items-center gap-2 whitespace-nowrap"
            >
              Mail-In Option
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          <div className="relative z-10 mt-6">
            <GoogleReviewsBadge />
          </div>
        </div>

        {/* Trust Signals - Social Proof */}
        <SellTrustSignals />

        {/* FAQ Section with Schema */}
        <SellFAQSection category="default" deviceName="device" />
        <div className="my-6"><SellContactSupport idPrefix="sell-footer" /></div>

        {/* Internal Links for SEO */}
        <SellInternalLinks exclude="/sell" />
      </div>

      {/* We Buy promotional banners */}
      <div className="bg-white">
        <BuybackBanners theme="light" />
      </div>

      {/* uBreakiFix-style SEO architecture block (additive) */}
      <div className="bg-white">
        <SellArchitectureBlock locality="Chicago" />
      </div>

      {/* Mobile Sticky CTA */}
      <MobileStickyQuoteCTA scrollToId="instant-estimate-section"
        contactAction={<HomeContactChoice kind="sell" idPrefix="sell-persistent" appearance="button" />} />

      {/* Related Services */}
      <RelatedServices type="sell" maxItems={6} />

      {/* Exit Intent Popup */}
      <ExitIntentPopup />
    </div>
  );
};

export default SellDevicePage;
